import Decimal from 'decimal.js';
import { Range, Chiku } from '../services/mapCodeTables.js';
import proj4 from 'proj4';

// WGS84 / Tokyo Datum の定義（proj4に無い場合があるので明示）
proj4.defs('EPSG:4326', '+proj=longlat +datum=WGS84 +no_defs');
proj4.defs('EPSG:4301', '+proj=longlat +ellps=bessel +towgs84=-148,507,685 +no_defs');

const SOURCE_DATUM = 'EPSG:4326';
const TARGET_DATUM = 'EPSG:4301';

/**
 * 緯度経度からマップコードを生成
 */
export function fetchMapCode(lat, lon, flg = 9) {
  if (lat == null || lon == null) return null;

  // wgsからtkyへ変換してから既存テーブルで計算
  try {
    const [txLon, txLat] = proj4(SOURCE_DATUM, TARGET_DATUM, [lon, lat]); // proj4は [lon,lat]
    lon = txLon;
    lat = txLat;
  } catch (e) {
  }

  const cmc = [5, 6, 7, 8, 0, 1, 2, 3, 4];

  // helper: DMS("deg/min/sec") or decimal degree -> 秒(Decimal), 切り捨て小数3桁
  function toSeconds(coord) {
    if (coord == null) return null;
    // Decimal 入力ならそのまま小数3桁切り捨て
    if (Decimal.isDecimal(coord)) {
      return coord.toDecimalPlaces(3, Decimal.ROUND_FLOOR);
    }
    // number -> 小数度を秒に
    if (typeof coord === 'number') {
      return new Decimal(coord).times(3600).toDecimalPlaces(3, Decimal.ROUND_FLOOR);
    }
    // string
    if (typeof coord === 'string') {
      if (coord.indexOf('/') !== -1) {
        const parts = coord.split('/');
        if (parts.length === 3) {
          const deg = new Decimal(parts[0]);
          const min = new Decimal(parts[1]);
          const sec = new Decimal(parts[2]);
          return deg.times(3600).plus(min.times(60)).plus(sec).toDecimalPlaces(3, Decimal.ROUND_FLOOR);
        }
      }
      const num = Number(coord);
      if (!Number.isNaN(num)) {
        return new Decimal(num).times(3600).toDecimalPlaces(3, Decimal.ROUND_FLOOR);
      }
    }
    return null;
  }

  const lon_sec = toSeconds(lon);
  const lat_sec = toSeconds(lat);
  if (!lon_sec || !lat_sec) return null;

  // Range.Areaは既存のテーブルを利用
  let Zn = -1;
  for (let cnt = 0; cnt < Range.Area.length; cnt++) {
    Zn = Range.Area[cnt][0];
    const rLonStart = new Decimal(Range.Area[cnt][1]);
    const rLonEnd = new Decimal(Range.Area[cnt][3]);
    const rLatStart = new Decimal(Range.Area[cnt][2]);
    const rLatEnd = new Decimal(Range.Area[cnt][4]);

    if (rLonStart.lte(lon_sec) && lon_sec.lte(rLonEnd) &&
      rLatStart.lte(lat_sec) && lat_sec.lte(rLatEnd)) {
      break;
    }
  }
  if (Zn === -1) return null;

  // オフセット
  const o_k = lon_sec.minus(new Decimal(Range.Area[Zn][1]));
  const o_i = lat_sec.minus(new Decimal(Range.Area[Zn][2]));

  // ブロック番号（切り捨て）
  const bn_k = o_k.dividedBy(30).floor().toNumber();
  const bn_i = o_i.dividedBy(30).floor().toNumber();

  // ユニット番号（Java の o_k.intValue() % 30 相当）
  const un_k = o_k.floor().minus(new Decimal(bn_k).times(30)).toNumber();
  const un_i = o_i.floor().minus(new Decimal(bn_i).times(30)).toNumber();

  // マップコード算出（標準部）
  const Bn = bn_i * 30 + bn_k;
  const Un = un_i * 30 + un_k;
  const MC = Zn * 1000000 + Bn * 1000 + Un;

  // コア／Sコア（高分解能）
  let CMC = null;
  if (flg === 3 || flg === 9) {
    const int_o_k = o_k.floor();
    const int_o_i = o_i.floor();

    const cn_k = o_k.minus(int_o_k).times(3).floor().toNumber();
    const cn_i = o_i.minus(int_o_i).times(3).floor().toNumber();

    const csn_k = o_k.minus(int_o_k).times(3).minus(cn_k).times(3).floor().toNumber();
    const csn_i = o_i.minus(int_o_i).times(3).minus(cn_i).times(3).floor().toNumber();

    const Cn = cn_i * 3 + cn_k;
    const Csn = csn_i * 3 + csn_k;

    const Cnc = cmc[Cn];
    const Csnc = cmc[Csn];

    CMC = (flg === 3) ? String(Cnc) : String(Cnc) + String(Csnc);
  }

  // MapCode文字列組み立て
  const Un2 = MC - Math.floor(MC / 1000) * 1000;
  const wk = Math.floor(MC / 1000);
  const Bn2 = wk - Math.floor(wk / 1000) * 1000;
  const Zn2 = Math.floor(wk / 1000);

  const pad3 = (n) => String(n).padStart(3, '0');
  let mapcode = '';
  if (Zn2 > 0) {
    mapcode = `${Zn2} ${pad3(Bn2)} ${pad3(Un2)}`;
  } else {
    mapcode = `${Bn2} ${pad3(Un2)}`;
  }
  if (CMC != null) mapcode += `*${CMC}`;

  return mapcode;
}

function dmsToDecimal(v) {
  if (v == null) return NaN;
  if (typeof v === 'number') return v;
  const s = String(v).trim();
  const parts = s.split('/');
  if (parts.length >= 3) {
    const deg = Number(parts[0]), min = Number(parts[1]), sec = Number(parts[2]);
    if ([deg, min, sec].some(x => !Number.isFinite(x))) return NaN;
    return deg + (min / 60) + (sec / 3600);
  }
  const n = Number(s);
  return Number.isFinite(n) ? n : NaN;
}

/**
 * マップコードから緯度経度を生成
 * @param {string} mapcode - マップコード (例: "12 345 678*12")
 * @returns {{lon: string, lat: string}|null}
 */
export function getLonLat(mapcode) {

  const cmc2 = [4, 5, 6, 7, 8, 0, 1, 2, 3];

  // マップコードを分解
  const [standardPart, cmcPart] = mapcode.split('*');
  const MC = parseInt(standardPart.replace(/ /g, ''), 10);
  const CMC = cmcPart || null;

  // ゾーン・ブロック・ユニット番号に分解
  const wk = Math.floor(MC / 1000);
  const Zn = Math.floor(wk / 1000);
  const Un = MC % 1000;
  const Bn = wk % 1000;

  // 高分解能部コードからミリ秒算出
  let Lon_ms = 0;
  let Lat_ms = 0;
  if (CMC) {
    const Cnc = parseInt(CMC[0], 10);
    const Csnc = CMC.length === 2 ? parseInt(CMC[1], 10) : 0;
    const Cn = cmc2[Cnc];
    const Csn = cmc2[Csnc];

    const onePer3 = new Decimal(1).dividedBy(3);
    const onePer9 = new Decimal(1).dividedBy(9);

    Lon_ms = onePer3.times(Cn % 3).plus(onePer9.times(Csn % 3)).times(1000).floor().toNumber();
    Lat_ms = onePer3.times(Math.floor(Cn / 3)).plus(onePer9.times(Math.floor(Csn / 3))).times(1000).floor().toNumber();
  }

  // ゾーンテーブルから経緯度算出
  for (let i = 0; i < Chiku.Area.length; i++) {
    if (Chiku.Area[i][0] === Zn) {
      // 経度
      let sec = Chiku.Area[i][2] * 3600 + Chiku.Area[i][3] * 60 + Chiku.Area[i][4] + ((Bn % Chiku.Area[i][1]) * 30 + (Un % 30));
      const lonH = Math.floor(sec / 3600);
      const lonS = sec % 60;
      const lonM = Math.floor((sec - lonS) % 3600 / 60);
      // const lon = `${lonH}/${String(lonM).padStart(2, '0')}/${String(lonS).padStart(2, '0')}.${Lon_ms}`;
      const lonDms = `${lonH}/${String(lonM).padStart(2, '0')}/${String(lonS).padStart(2, '0')}.${Lon_ms}`;

      // 緯度
      sec = Chiku.Area[i][5] * 3600 + Chiku.Area[i][6] * 60 + Chiku.Area[i][7] + (Math.floor(Bn / Chiku.Area[i][1]) * 30 + Math.floor(Un / 30));
      const latH = Math.floor(sec / 3600);
      const latS = sec % 60;
      const latM = Math.floor((sec - latS) % 3600 / 60);
      const latDms = `${latH}/${String(latM).padStart(2, '0')}/${String(latS).padStart(2, '0')}.${Lat_ms}`;

      // DMS(tky) -> decimal(tky)
      const tkyLon = dmsToDecimal(lonDms);
      const tkyLat = dmsToDecimal(latDms);
      if (!Number.isFinite(tkyLon) || !Number.isFinite(tkyLat)) return null;

      // tky(EPSG:4301) -> wgs(EPSG:4326)
      try {
        // proj4 は [lon,lat]
        const [wgsLon, wgsLat] = proj4(TARGET_DATUM, SOURCE_DATUM, [tkyLon, tkyLat]);
        if (!Number.isFinite(wgsLon) || !Number.isFinite(wgsLat)) {
          return null;
        }
        return {
          lon: wgsLon,
          lat: wgsLat
        };
      } catch (e) {
        return null;
      }
    }
  }
  return null;
}